import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { analytics } from './firebase';
import { logEvent } from 'firebase/analytics';
// Import MUI icons
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import Profile from './components/Profile';
import Playbooks from './components/Playbooks';
import Picks from './components/Picks';
import Contact from './components/Contact';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import PlaybookBuilder from './components/PlaybookBuilder';
import SimpleBuilder from './components/SimpleBuilder';
import CanvasCursor from './components/CanvasCursor';
import PlaybookView from './components/PlaybookView';
import NotFound from './components/NotFound';
import LanguageBuilder from './components/LanguageBuilder';
import './App.css';

// Protected Route component
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return user ? children : <Navigate to="/login" />;
}

// Public Route component (redirects to home if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return !user ? children : <Navigate to="/" />;
}

// FAQ Item Component
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`} onClick={onClick}>
      <div className="faq-question">
        <h3>{question}</h3>
        <div className="faq-arrow"></div>
      </div>
      <div className="faq-answer">
        {Array.isArray(answer) ? answer.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        )) : <p>{answer}</p>}
      </div>
    </div>
  );
}

// Home component
function Home() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const navigate = useNavigate();
  const heroRef = useRef(null); // Add ref for hero section

  const faqData = [
    {
      question: "How is Odikai better?",
      answer: [
        "Odikai isn't just another pick recommendation platform.",
        "We build hundreds of strategies for betting on the UFC and test their historical performance to pick the ones that made the most money while minimizing losses. Our playbook building tool lets you use that same technology to test your own strategies and find a system that aligns with how you already place bets.",
        "It's similar to picking stocks for a portfolio, and we've adapted our systems from the same tools that investors use to make long-term money in the stock market.",
        "Every boxer doesn't need to win by KO - sometimes a split decision is all it takes to get the win. That's how we approach betting: winning is always better than losing, even if it isn't flashy."
      ]
    },
    {
      question: "How is Odikai different?",
      answer: [
        "We don't treat sports betting like Moneyball - we view it as any other appreciating asset.",
        "That means we're not just focused on predicting winners; we're also maximizing returns, minimizing volatility, and trading moonshot guesses for reliable, long-term growth.",
        "Our unique strategy selection method allows us to analyze each match from multiple perspectives, relying on real historical performance (not black-box AI models) to determine the best approach.",
        "The best coaches enter games armed with dozens of strategies to adapt and win, no matter the circumstances. Fighters step into the ring with a combination of refined skills and tools, all working together to get the win. So why rely on just one prediction model?"
      ]
    },
    {
      question: "How much does Odikai cost?",
      answer: [
        "To be honest, we're not sure yet! We're currently working on our subscription service.",
        "Our AI-powered picks are, and always will be, free. If you want to get notified when you can subscribe for our advanced playbook-building tools, AI-powered strategies, and more leagues, create a free account."
      ]
    },
    {
      question: "Can I place bets on Odikai?",
      answer: [
        "No.",
        "We are not a sportsbook and do not allow you to place bets directly through Odikai. Our recommended picks can be placed using any major sportsbook, and the odds we display are based on the average odds from several betting platforms."
      ]
    },
    {
      question: "What leagues does Odikai support?",
      answer: [
        "Right now, Odikai focuses exclusively on the UFC. We chose to start with a league that is both challenging and less extensively researched than others, and we believe we succeeded.",
        "We're working on building playbook building tools for other leagues (NBA, NFL, NCAA, etc.), though.",
        "Create a free account to be notified when we release new tools and be among the first to build a playbook for your favorite league."
      ]
    }
  ];

  const handleFAQClick = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleViewPicks = () => {
    navigate('/picks');
  };

  return (
    <div className="home">
      <section className="hero" ref={heroRef}>
        <CanvasCursor containerRef={heroRef} />
        <div className="hero-content">
          <h1>Bet like an Investor</h1>
          <p className="hero-subtitle">Beat the sportsbooks with the tech that powers Wall Street.</p>
          <button className="cta-button" onClick={handleViewPicks}>
            View Picks
            <ArrowForwardIcon />
          </button>
        </div>
      </section>

      <section className="approach-section">
        <div className="approach-content">
          <div className="approach-intro">
            <h2 className="approach-header">Winning picks don't guarantee <span className="highlight-text">long term profits</span>.</h2>
            <p className="approach-subheader">Anyone can pick a few winners, and AI prediction tools are a dime a dozen. <span className="approach-emphasis">We take a different approach...</span></p>
          </div>
          <div className="approach-grid">
            <div className="approach-card">
              <div className="approach-card-header">
                <BarChartIcon className="approach-card-icon" />
                <h3>Strategy-First Wagers</h3>
              </div>
              <p>
                Odikai lets you choose your unique combination of betting strategies and turns them into a playbook that is optimized and fine-tuned to maximize every dollar you bet.
              </p>
              <p>
                Our goal is to refine how you approach betting by focusing on empowering your strategy instead of handing you picks from our team or some opaque AI model.
              </p>
            </div>
            <div className="approach-card">
              <div className="approach-card-header">
                <AutoGraphIcon className="approach-card-icon" />
                <h3>Free AI Predictions</h3>
              </div>
              <p>
                All of our AI predictions are, and always will be, free. Our founding team believes that anyone selling picks, AI powered or not, is just taking advantage of you.
              </p>
              <p>
                Why would they sell picks if they are actually making money?
              </p>
            </div>
            <div className="approach-card">
              <div className="approach-card-header">
                <ShowChartIcon className="approach-card-icon" />
                <h3>Data-Driven Decisions</h3>
              </div>
              <p>
                Our unique tools combine advanced statistical analysis with traditional betting strategies and machine learning to find the most profitable way to distribute your bets. Everything is built to align with how you already bet.
              </p>
              <p>
                Sportsbooks are using everything they can to stop you from winning. Odikai gives you back the advantage.
              </p>
            </div>
            <div className="approach-card">
              <div className="approach-card-header">
                <DoNotDisturbIcon className="approach-card-icon" />
                <h3>Cycle Breaking Betting</h3>
              </div>
              <p>
                Betting apps want you in the app constantly. They want you placing more bets when you lose and depositing more when you run out. Odikai is built to break that cycle.
              </p>
              <p>
                Enter your budget, pick your strategy, place your bets, then close the app and enjoy your life. Odikai's AI powered picks help you stay profitable without being glued to your phone.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="faq-content">
          <h2>Common Questions</h2>
          <div className="faq-list">
            {faqData.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => handleFAQClick(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="etymology-section">
        <div className="etymology-content">
          <div className="etymology-text">
            <p className="definition">
              The <span className="term">Hellan<span className="term-highlight">odikai</span></span> (Ancient Greek: Ἑλλανοδίκαι) were the judges of the Ancient Olympic Games, and the success of the games was attributed to their efforts.
              <a href="https://en.wikipedia.org/wiki/Hellanodikai" className="source-link" target="_blank" rel="noopener noreferrer">(wikipedia)</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

// Main App Layout component
function AppLayout() {
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const mainRef = useRef(null);

  // Track page views
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_path: pathname,
      page_title: document.title
    });
  }, [pathname]);

  // Scroll to top when pathname changes
  useEffect(() => {
    // Force scroll to top in multiple ways
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [pathname]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Remove all console.error statements
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.classList.toggle('menu-open');
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.classList.remove('menu-open');
  };

  useEffect(() => {
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, []);

  return (
    <div className="app">
      <nav className={isMenuOpen ? 'open' : ''}>
        <div className="nav-content">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img src="/images/logo.png" alt="Odikai Logo" className="logo-image" />
            <span className="logo-text">Odikai</span>
          </Link>
          <button className="menu-toggle" onClick={toggleMenu}>
            <span></span>
            <span></span>
            <span></span>
          </button>
          <ul>
            <li>
              <Link to="/" onClick={closeMenu}>
                <HomeIcon /> Home
              </Link>
            </li>
            <li>
              <Link to="/picks" onClick={closeMenu}>
                <BarChartIcon /> Picks
              </Link>
            </li>
            {user ? (
              <>
                <li>
                  <Link to="/playbooks" onClick={closeMenu}>
                    <MenuBookIcon /> Playbooks
                  </Link>
                </li>
                <li>
                  <Link to="/profile" onClick={closeMenu}>
                    <AccountCircleIcon /> Profile
                  </Link>
                </li>
                <li>
                  <button onClick={handleLogout} className="nav-button">
                    <LogoutIcon /> Logout
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link to="/login" onClick={closeMenu}>
                    <LoginIcon /> Login
                  </Link>
                </li>
                <li>
                  <Link to="/register" onClick={closeMenu}>
                    <PersonAddIcon /> Register
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </nav>

      <main ref={mainRef}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

// Root App component
function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route element={<AppLayout />}>
            <Route index element={<Home />} />
            <Route path="/picks" element={<Picks />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/playbooks" element={<PrivateRoute><Playbooks /></PrivateRoute>} />
            <Route path="/playbooks/:id" element={<PrivateRoute><PlaybookView /></PrivateRoute>} />
            <Route path="/playbooks/build" element={<PrivateRoute><PlaybookBuilder /></PrivateRoute>} />
            <Route path="/playbooks/build/simple" element={<PrivateRoute><SimpleBuilder /></PrivateRoute>} />
            <Route path="/playbooks/build/language" element={<PrivateRoute><LanguageBuilder /></PrivateRoute>} />
            <Route path="/contact" element={<PrivateRoute><Contact /></PrivateRoute>} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
